import { SITE_DOMAIN_LABEL } from '../constants/site';
import { useLanguage } from '../i18n/LanguageContext';
import ShareCardQrCode from './ShareCardQrCode';
import {
  SHARE_CARD_FONT,
  SHARE_CARD_PREVIEW_WIDTH,
  createShareCardMetrics,
  getFrozenHeartCardTheme,
  getPersonalityCardTheme,
  getToneTheme,
  type ShareCardAspect,
  type ShareCardStat,
  type ShareCardTheme,
} from '../utils/shareCardHelpers';

export interface QuizShareCardProps {
  id: string;
  variant: 'personality' | 'single' | 'bedroom';
  aspect?: ShareCardAspect;
  shareTitle: string;
  shareHook: string;
  percent: number;
  typeKey: string;
  variantKey?: string;
  displayName?: string;
  city?: string;
  zodiac?: string;
  zodiacSymbol?: string;
  avatarUrl?: string;
  temperature?: string;
  temperatureLabel?: string;
  triggerTagD?: boolean;
  triggerTagDBadge?: string;
  stats?: ShareCardStat[];
  progressBars?: { icon: string; label: string }[]; // for bedroom fixed bars
  /** When true, the card root uses width/height 100% to fill its parent container (for gallery, teaser, grids). Parent should provide aspect-ratio. */
  fitContainer?: boolean;
  /** When fitContainer, reference width used to compute internal px sizes (visual density). Smaller for grid cards, ~360 for large views. */
  displayWidth?: number;
}

const VARIANT_STYLES = {
  personality: {
    border: '#d4af37',
    accent: '#d4af37',
    hookColor: '#fde68a',
    gradient: 'linear-gradient(180deg, #0a0a0a 0%, #1a1520 55%, #0a0a0a 100%)',
    emoji: '🎭',
  },
  single: {
    border: '#67e8f9',
    accent: '#7dd3fc',
    hookColor: '#fda4af',
    gradient: 'linear-gradient(180deg, #0a0a0a 0%, #0c1929 45%, #0f172a 70%, #0a0a0a 100%)',
    emoji: '🧊',
  },
  bedroom: {
    border: '#ff1493',
    accent: '#ff1493',
    hookColor: '#ffb6c1',
    gradient: 'linear-gradient(180deg, #0a0a0a 0%, #2a0f1f 55%, #0a0a0a 100%)',
    emoji: '🛏️',
  },
} as const;

const READABILITY_SHADOW = '0 1px 6px rgba(0,0,0,0.5)';

function StatBar({
  stat,
  accent,
  textMuted,
  px,
}: {
  stat: ShareCardStat;
  accent: string;
  textMuted: string;
  px: (value: number) => number;
}) {
  const hasPercent = typeof stat.percent === 'number';

  return (
    <div style={{ marginBottom: px(5), textAlign: 'left' }}>
      <div
        style={{
          fontSize: px(8),
          color: textMuted,
          marginBottom: px(2),
          lineHeight: 1.3,
          fontFamily: SHARE_CARD_FONT,
          textShadow: READABILITY_SHADOW,
        }}
      >
        <span style={{ marginRight: px(4), display: 'inline-block' }}>{stat.icon}</span>
        <span>
          {stat.label}
          {hasPercent ? `: ${stat.percent}%` : stat.textValue ? `: ${stat.textValue}` : ''}
        </span>
      </div>
      {hasPercent && (
        <div
          style={{
            height: px(4),
            borderRadius: px(2),
            background: 'rgba(255,255,255,0.08)',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              width: `${stat.percent}%`,
              height: '100%',
              borderRadius: px(2),
              background: `linear-gradient(90deg, ${accent}88, ${accent})`,
            }}
          />
        </div>
      )}
    </div>
  );
}

function ProfileAvatar({
  avatarUrl,
  emoji,
  border,
  px,
  size = 56,
}: {
  avatarUrl?: string;
  emoji: string;
  border: string;
  px: (value: number) => number;
  size?: number;
}) {
  const avatarSize = px(size);
  const sharedStyle = {
    width: avatarSize,
    height: avatarSize,
    borderRadius: '50%',
    border: `${px(2)}px solid ${border}`,
    flexShrink: 0,
  } as const;

  if (avatarUrl) {
    return (
      <div
        data-share-avatar-frame
        style={{
          ...sharedStyle,
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        <img
          src={avatarUrl}
          alt=""
          data-share-avatar-img
          crossOrigin="anonymous"
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            objectPosition: 'center',
            display: 'block',
          }}
        />
      </div>
    );
  }

  return (
    <div
      style={{
        ...sharedStyle,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(255,255,255,0.06)',
        fontSize: px(size * 0.46),
      }}
    >
      {emoji}
    </div>
  );
}

export default function QuizShareCard({
  id,
  variant,
  aspect = 'story',
  shareTitle,
  shareHook,
  percent,
  variantKey,
  displayName,
  city,
  zodiac,
  zodiacSymbol,
  avatarUrl,
  temperature,
  temperatureLabel,
  triggerTagD,
  triggerTagDBadge,
  stats = [],
  progressBars,
  fitContainer,
  displayWidth,
}: QuizShareCardProps) {
  const { m } = useLanguage();
  const cardLabels = m.share.card;
  const { spec, px: designPx, isFeed, hookLineClamp } = createShareCardMetrics(aspect);

  const isFitting = !!fitContainer;
  // Choose a display reference width for px() math when fitting (controls text/padding density relative to container).
  // Parents provide aspect-ratio box; this sets how "zoomed" the design looks inside it.
  const effectiveDisplayWidth = isFitting
    ? (displayWidth || (aspect === 'story' ? 360 : 300))
    : spec.width;

  const px = (value: number) => {
    if (!isFitting) return designPx(value);
    const scale = effectiveDisplayWidth / SHARE_CARD_PREVIEW_WIDTH;
    const compact = isFeed ? 0.84 : 1;
    return Math.round(value * compact * scale);
  };

  const baseStyle = VARIANT_STYLES[variant];
  const variantLabel = variant === 'personality' 
    ? cardLabels.personalityLabel 
    : variant === 'single' 
      ? cardLabels.singleLabel 
      : 'Cấu hình phòng ngủ';
  const cardTheme: ShareCardTheme =
    variant === 'personality'
      ? getPersonalityCardTheme(variantKey)
      : variant === 'single'
        ? getFrozenHeartCardTheme(variantKey)
        : { ...baseStyle, textPrimary: '#f8fafc', textMuted: '#e2e8f0', textFooter: '#cbd5e1', surfaceBg: 'rgba(0,0,0,0.24)', surfaceBorder: 'rgba(255,255,255,0.14)' }; // bedroom theme
  const style = { ...baseStyle, ...cardTheme };
  const isFrozenHeart = variant === 'single' && temperature;
  const isBedroom = variant === 'bedroom';
  const hasProfileInfo = Boolean(displayName || city || zodiac);
  const avatarSize = isFeed ? 50 : 56;

  // Compute heat level from result to determine glowing tone (redder = hotter/passionate, bluer = lowkey/icy)
  // Slight variation from variantKey for "random" feel within the result
  let resultHeat = 0.5;
  const resultType = variantKey ? variantKey[0] : 'A';
  if (variant === 'personality') {
    resultHeat = ({ A: 0.3, B: 0.85, C: 0.5, D: 0.65 } as any)[resultType] || 0.5;
  } else if (variant === 'single') {
    if (temperature) {
      const t = parseFloat(temperature) || -40;
      resultHeat = Math.max(0.15, Math.min(0.75, (t + 90) / 100));
    } else {
      resultHeat = ({ A: 0.35, B: 0.5, C: 0.65 } as any)[resultType] || 0.5;
    }
  } else if (variant === 'bedroom') {
    resultHeat = ({ A: 0.2, B: 0.5, C: 0.75, D: 0.9 } as any)[resultType] || 0.5;
  }
  if (variantKey) {
    const r = (variantKey.charCodeAt(0) + (variantKey.charCodeAt(1) || 0)) % 5;
    resultHeat += (r - 2) * 0.03;
    resultHeat = Math.max(0.1, Math.min(0.95, resultHeat));
  }
  const tone = getToneTheme(resultHeat);
  const finalStyle = {
    ...style,
    gradient: tone.gradient,
    border: tone.border,
    accent: tone.accent,
    hookColor: tone.hookColor || style.hookColor,
    glow: tone.glow || style.glow,
    textPrimary: tone.textPrimary || style.textPrimary,
    textMuted: tone.textMuted || style.textMuted,
    textFooter: tone.textFooter || style.textFooter,
    surfaceBg: tone.surfaceBg || style.surfaceBg,
    surfaceBorder: tone.surfaceBorder || style.surfaceBorder,
  };

  return (
    <div
      id={id}
      style={{
        width: isFitting ? '100%' : spec.width,
        height: isFitting ? '100%' : spec.height,
        background: finalStyle.gradient,
        border: `${px(3)}px solid ${finalStyle.border}`,
        borderRadius: px(20),
        padding: `${px(isFeed ? 14 : 18)}px ${px(16)}px ${px(isFeed ? 10 : 14)}px`,
        position: 'relative',
        boxShadow: `0 ${px(12)}px ${px(40)}px rgba(0,0,0,0.55)`,
        fontFamily: SHARE_CARD_FONT,
        color: finalStyle.textPrimary,
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
        overflow: 'hidden',
        isolation: 'isolate',
      }}
    >
      {finalStyle.glow && (
        <div
          aria-hidden
          style={{
            position: 'absolute',
            inset: 0,
            background: finalStyle.glow,
            pointerEvents: 'none',
            borderRadius: px(20),
            zIndex: 0,
          }}
        />
      )}

      <div
        style={{
          position: 'relative',
          zIndex: 1,
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
        }}
      >
      {triggerTagD && triggerTagDBadge && (
        <div
          style={{
            position: 'absolute',
            top: px(10),
            right: px(-4),
            transform: 'rotate(12deg)',
            background: 'linear-gradient(135deg, #ff006e 0%, #fb7185 50%, #f43f5e 100%)',
            color: '#fff',
            fontSize: px(7),
            fontWeight: 700,
            padding: `${px(4)}px ${px(8)}px`,
            borderRadius: px(4),
            lineHeight: 1.3,
            textAlign: 'center',
            boxShadow: `0 0 ${px(14)}px rgba(255, 0, 110, 0.75)`,
            letterSpacing: '0.03em',
            border: '1px solid rgba(255,255,255,0.35)',
            zIndex: 2,
            maxWidth: px(118),
            fontFamily: SHARE_CARD_FONT,
          }}
        >
          {triggerTagDBadge}
        </div>
      )}

      <div style={{ flexShrink: 0, textAlign: 'center', marginBottom: px(isFeed ? 8 : 10) }}>
        <div
          style={{
            fontSize: px(9),
            letterSpacing: '0.08em',
            color: finalStyle.accent,
            fontWeight: 700,
          }}
        >
          ✧ {variantLabel} ✧
        </div>
      </div>

      {hasProfileInfo && (
        <div
          style={{
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: px(10),
            marginBottom: px(isFeed ? 8 : 10),
            width: '100%',
          }}
        >
          <ProfileAvatar
            avatarUrl={avatarUrl}
            emoji={style.emoji}
            border={finalStyle.border}
            px={px}
            size={avatarSize}
          />
          <div style={{ minWidth: 0, textAlign: 'left' }}>
            {displayName && (
              <div
                style={{
                  fontSize: px(isFeed ? 13 : 14),
                  color: style.textPrimary,
                  fontWeight: 700,
                  lineHeight: 1.25,
                  marginBottom: px(3),
                  wordBreak: 'break-word',
                  fontFamily: SHARE_CARD_FONT,
                  textShadow: READABILITY_SHADOW,
                }}
              >
                {displayName}
              </div>
            )}
            {city && (
              <div
                style={{
                  fontSize: px(10),
                  color: style.textMuted,
                  lineHeight: 1.35,
                  fontFamily: SHARE_CARD_FONT,
                  fontWeight: 400,
                }}
              >
                {city}
              </div>
            )}
            {zodiac && (
              <div
                style={{
                  fontSize: px(10),
                  color: style.textMuted,
                  lineHeight: 1.35,
                  marginTop: px(2),
                  fontFamily: SHARE_CARD_FONT,
                  fontWeight: 400,
                }}
              >
                {zodiac}
                {zodiacSymbol ? ` ${zodiacSymbol}` : ''}
              </div>
            )}
          </div>
        </div>
      )}

      {!hasProfileInfo && (
        <div
          style={{
            flexShrink: 0,
            display: 'flex',
            justifyContent: 'center',
            marginBottom: px(isFeed ? 8 : 10),
          }}
        >
          <ProfileAvatar
            avatarUrl={avatarUrl}
            emoji={style.emoji}
            border={finalStyle.border}
            px={px}
            size={avatarSize}
          />
        </div>
      )}

      {stats.length > 0 && (
        <div
          style={{
            flexShrink: 0,
            background: finalStyle.surfaceBg,
            border: `1px solid ${finalStyle.surfaceBorder}`,
            borderRadius: px(8),
            padding: `${px(isFeed ? 5 : 6)}px ${px(8)}px`,
            marginBottom: px(isFeed ? 6 : 8),
          }}
        >
          {stats.map((stat) => (
            <StatBar
              key={stat.label}
              stat={stat}
              accent={finalStyle.accent}
              textMuted={style.textMuted}
              px={px}
            />
          ))}
        </div>
      )}

      {isBedroom && progressBars && progressBars.length > 0 && (
        <div
          style={{
            flexShrink: 0,
            background: finalStyle.surfaceBg,
            border: `1px solid ${finalStyle.surfaceBorder}`,
            borderRadius: px(8),
            padding: `${px(isFeed ? 5 : 6)}px ${px(8)}px`,
            marginBottom: px(isFeed ? 6 : 8),
            fontSize: px(8),
            color: style.textMuted,
            lineHeight: 1.3,
          }}
        >
          {progressBars.map((bar, i) => (
            <div key={i} style={{ marginBottom: px(2) }}>
              {bar.icon} {bar.label}
            </div>
          ))}
        </div>
      )}

      <div
        style={{
          flex: '1 1 auto',
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          textAlign: 'center',
          overflow: 'hidden',
        }}
      >
        {isBedroom && (
          <div
            style={{
              fontSize: px(isFeed ? 9 : 10),
              color: '#ff1493',
              fontWeight: 700,
              letterSpacing: '0.08em',
              marginBottom: px(4),
              textShadow: `0 0 ${px(8)}px #ff149333`,
            }}
          >
            ✧ Cấu hình phòng ngủ ✧
          </div>
        )}
        <div
          style={{
            fontSize: (shareTitle || '').includes('|')
              ? px(isFeed ? 12 : 13)
              : isFrozenHeart
                ? px(isFeed ? 13 : 14)
                : px(isFeed ? 14 : 15),
            fontWeight: 700,
            color: finalStyle.accent,
            lineHeight: 1.3,
            marginBottom: px(isFeed ? 6 : 8),
            textShadow: `0 0 ${px(10)}px ${style.accent}33`,
          }}
        >
          {(shareTitle || '').split('|').map((part, i) => (
            <div key={i} style={{ marginBottom: i === 0 && (shareTitle || '').includes('|') ? px(4) : 0 }}>
              {part.trim()}
            </div>
          ))}
        </div>
        {isFrozenHeart && (
          <div
            style={{
              fontSize: px(10),
              color: style.textMuted,
              marginBottom: px(isFeed ? 6 : 8),
              lineHeight: 1.4,
              textShadow: READABILITY_SHADOW,
            }}
          >
            {cardLabels.frozenPercent}{' '}
            <strong style={{ color: style.accent }}>{percent}%</strong>
            {' · '}{cardLabels.temperature}{' '}
            <strong style={{ color: style.accent }}>{temperature}</strong>
            {temperatureLabel ? ` (${temperatureLabel})` : ''}
          </div>
        )}
        <p
          style={{
            fontSize: px(isFeed ? 10 : 11),
            lineHeight: 1.45,
            color: finalStyle.hookColor,
            margin: 0,
            fontStyle: 'italic',
            fontWeight: 500,
            fontFamily: SHARE_CARD_FONT,
            display: '-webkit-box',
            WebkitLineClamp: hookLineClamp,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textShadow: `0 ${px(1)}px ${px(4)}px rgba(0,0,0,0.5)`,
          }}
        >
          “{shareHook}”
        </p>
      </div>

      <div style={{ flexShrink: 0, marginTop: px(isFeed ? 6 : 8) }}>
        <ShareCardQrCode
          accent={style.accent}
          cardWidth={isFitting ? effectiveDisplayWidth : spec.width}
          compact={isFeed || isFitting}
          surfaceBg={style.surfaceBg}
          surfaceBorder={style.surfaceBorder}
          textMuted={style.textMuted}
          subtitle={isFrozenHeart ? cardLabels.qrSubtitleSingle : cardLabels.qrSubtitlePersonality}
        />
        <div
          style={{
            fontSize: px(9),
            color: finalStyle.accent,
            fontWeight: 700,
            marginTop: px(isFeed ? 4 : 6),
            textAlign: 'center',
          }}
        >
          {SITE_DOMAIN_LABEL}
        </div>
        <div
          style={{
            fontSize: px(8),
            color: finalStyle.textFooter,
            marginTop: px(2),
            textAlign: 'center',
            lineHeight: 1.3,
          }}
        >
          {cardLabels.footerTagline}
        </div>
      </div>
      </div>
    </div>
  );
}